import type React from "react"

import { ConfirmationDialog } from "./ConfirmDialog"

/** Noun used in the dialog copy, e.g. "Make this simulation public?". */
type PublicResourceKind = "simulation" | "conversation" | "run" | "report"

interface MakePublicConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  resource: PublicResourceKind
  /** How many of the resource the action publishes at once. Defaults to one. */
  count?: number
  /** Extra sentence for a resource whose scope is not obvious, e.g. a report that publishes every run in it. */
  detail?: string
  /** Public link is gated by a password the user is about to set. */
  passwordProtected?: boolean
  pending?: boolean
}

const RESOURCE_LABEL: Record<PublicResourceKind, string> = {
  simulation: "simulation",
  conversation: "conversation",
  run: "run",
  report: "report",
}

export const MakePublicConfirmDialog: React.FC<
  MakePublicConfirmDialogProps
> = ({
  open,
  onOpenChange,
  onConfirm,
  resource,
  count = 1,
  detail,
  passwordProtected = false,
  pending,
}) => {
  const label = RESOURCE_LABEL[resource]
  const isPlural = count > 1
  const subject = isPlural ? `these ${count} ${label}s` : `this ${label}`
  const link = isPlural ? "the links" : "the link"
  const access = passwordProtected ? `${link} and the password` : link

  return (
    <ConfirmationDialog
      showConfirmDialog={open}
      setShowConfirmDialog={onOpenChange}
      handleConfirmChange={onConfirm}
      title={`Make ${subject} public?`}
      description={
        <span className="flex flex-col gap-2">
          <span>
            {`Anyone with ${access} can open ${subject} without a Coval account, including ${isPlural ? "their" : "its"} transcript and metric results.`}
          </span>
          {detail ? <span>{detail}</span> : null}
          <span>
            {isPlural
              ? "You can make them private again at any time."
              : "You can make it private again at any time."}
          </span>
        </span>
      }
      confirmLabel="Make public"
      pending={pending}
    />
  )
}
